/**
 * Module dependencies.
 */

import { Button, Input } from 'src/components/core';
import { NextPage } from 'next/types';
import { authRoutes } from 'src/core/routes';
import { recoverPassword } from 'src/api/auth/recover-password';
import { regexes } from 'src/core/utils/regexes';
import { units } from '@untile/react-components';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import BaseAuthPage from 'src/components/pages/auth';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled(Input)`
  margin-bottom: ${units(4.5)};
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  max-width: ${units(58)};
  width: 100%;
`;

/**
 * `PasswordRecovery` page.
 */

const PasswordRecovery: NextPage = () => {
  const router = useRouter();
  const { showMessage } = useSnackbar();
  const { translate } = useTranslate();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset
  } = useForm({
    mode: 'onBlur'
  });

  const { isLoading: isSubmitting, mutate } = useMutation(recoverPassword);

  const onSubmit = useCallback(
    ({ email }) => {
      mutate(
        { email },
        {
          onError: () => {
            showMessage(translate(`errors:defaultError`), {
              appearance: 'error'
            });
          },
          onSuccess: () => {
            reset();
            router.push(authRoutes.signIn);

            showMessage(translate('password:recover.successMessage'), {
              appearance: 'success'
            });
          }
        }
      );
    },
    [mutate, reset, router, showMessage, translate]
  );

  return (
    <BaseAuthPage
      description={translate('password:recover.description')}
      title={translate('password:recover.title')}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledInput
          disabled={isSubmitting}
          error={errors?.email}
          isRequired
          label={translate('forms:labels.email')}
          name={'email'}
          placeholder={translate('forms:placeholders.email')}
          {...register('email', {
            pattern: {
              message: translate('forms:rules.email'),
              value: regexes.email
            },
            required: {
              message: translate('forms:rules.required'),
              value: true
            }
          })}
        />

        <StyledButton
          isLoading={isSubmitting}
          type={'submit'}
        >
          {translate('common:actions.resetPassword')}
        </StyledButton>
      </form>
    </BaseAuthPage>
  );
};

/**
 * Export `PasswordRecovery` page.
 */

export default PasswordRecovery;
